import { useState, type ReactNode } from "react";
import { Download, Maximize2, Upload } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import type { ArtifactPreview } from "~/services/artifacts";
import { DownloadDialog } from "./DownloadDialog";
import { ExportDialog } from "./ExportDialog";

interface ArtifactToolbarProps {
  preview: ArtifactPreview;
  // Optional expand-to-modal action — wired by the shell when the
  // renderer supplies an `expandedContent` slot.
  onExpand?: () => void;
}

// Icon-only action row shown on the right side of every artifact card
// header. Tooltips provide labels. Layout:
//   [↓] [↑] [⤢]?
// Download/Export each open a modal that handles workbench/format
// selection and progress — no inline caret menu. Expand only renders
// when the artifact shell was given an expanded view.
export const ArtifactToolbar = ({ preview, onExpand }: ArtifactToolbarProps) => {
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Share-token viewers don't have a conversationId, and the download/export
  // endpoints aren't token-scoped — hide the actions rather than render
  // buttons that 404. Expand is local-only, so it stays.
  const { conversationId } = useConversationContext();
  const showActions = conversationId !== null;

  return (
    <>
      <div className="flex items-center gap-0.5">
        {showActions && (
          <>
            <IconAction
              label="Download"
              icon={<Download className="size-3.5" />}
              onClick={() => setDownloadOpen(true)}
            />
            <IconAction
              label="Export to workbench"
              icon={<Upload className="size-3.5" />}
              onClick={() => setExportOpen(true)}
            />
          </>
        )}
        {onExpand && (
          <IconAction label="Expand" icon={<Maximize2 className="size-3.5" />} onClick={onExpand} />
        )}
      </div>
      {showActions && (
        <>
          <DownloadDialog
            open={downloadOpen}
            onClose={() => setDownloadOpen(false)}
            artifactId={preview.artifact_id}
            artifactType={preview.artifact_type}
          />
          <ExportDialog
            open={exportOpen}
            onClose={() => setExportOpen(false)}
            artifactId={preview.artifact_id}
            artifactType={preview.artifact_type}
          />
        </>
      )}
    </>
  );
};

interface IconActionProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

const IconAction = ({ label, icon, onClick, disabled }: IconActionProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={onClick}
        aria-label={label}
        className="h-7 w-7 p-0 text-muted-foreground"
      >
        {icon}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);
